import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import Button from '../../components/Button';
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import CommonToastContainer from '../../components/CommonToastContainer';
import DeleteModal from '../../components/DeleteModal';

const MissionAdmin = () => {
  const [missions, setMissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentMission, setCurrentMission] = useState({ id: null, title: '', description: '' });
  const [missionToDelete, setMissionToDelete] = useState(null);

  // Fallback to fetch if there is an endpoint, otherwise keep empty
  const fetchMissions = async () => {
    setIsLoading(true);
    try {
      // Assuming there is a get API, if not it will just fail gracefully
      const getApi = apiUrls.getmissioncard || '/ecomcourse/missioncard/getmissioncard';
      const response = await postRequest(getApi, {});
      if (response && response.status === "S" && Array.isArray(response.data)) {
        setMissions(response.data);
      } else if (Array.isArray(response)) {
        setMissions(response);
      }
    } catch (error) {
      console.log("No getmissioncard api or error fetching missions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMissions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentMission({ ...currentMission, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentMission.title || !currentMission.description) {
      toast.error('Title and description are required.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        title: currentMission.title,
        description: currentMission.description,
      };
      if (currentMission.id || currentMission.rcid) {
        const idValue = currentMission.rcid || currentMission.id;
        payload.rcid = idValue;
        payload.id = idValue;
        payload.missionid = idValue;
        payload.missioncardid = idValue;
      }
      
      const apiUrl = isEditing ? apiUrls.updatemissioncard : apiUrls.createmissioncard;
      const response = await postRequest(apiUrl, payload);
      
      if (response && (response.status === "S" || response.success !== false)) {
        toast.success(isEditing ? 'Mission updated successfully!' : 'Mission added successfully!');
        setCurrentMission({ id: null, title: '', description: '' });
        setIsEditing(false);
        fetchMissions();
      } else {
        toast.error(response?.message || 'Failed to save mission.');
      }
    } catch (error) {
      console.error('Error saving mission:', error);
      toast.error('An error occurred while saving.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (mission) => {
    setCurrentMission({
      id: mission.id || mission.rcid,
      rcid: mission.rcid || mission.id,
      title: mission.title || '',
      description: mission.description || ''
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setCurrentMission({ id: null, title: '', description: '' });
    setIsEditing(false);
  };

  const handleDeleteClick = (mission) => {
    setMissionToDelete(mission);
  };

  const handleConfirmDelete = async () => {
    if (!missionToDelete) return;
    
    setIsLoading(true);
    try {
      const idValue = missionToDelete.rcid || missionToDelete.id;
      const payload = {
        rcid: idValue,
        id: idValue,
        missionid: idValue,
        missioncardid: idValue
      };
      const response = await postRequest(apiUrls.deletemissioncard, payload);
      if (response && (response.status === "S" || response.success !== false)) {
        toast.success('Mission deleted successfully!');
        fetchMissions();
        if ((currentMission.id && currentMission.id === idValue) || (currentMission.rcid && currentMission.rcid === idValue)) {
          handleCancelEdit();
        }
      } else {
        toast.error(response?.message || 'Failed to delete mission.');
      }
    } catch (error) {
      console.error('Error deleting mission:', error);
      toast.error('An error occurred while deleting.');
    } finally {
      setIsLoading(false);
      setMissionToDelete(null);
    }
  };

  return (
    <div className="bg-[#F8F9FE] text-zinc-900 antialiased h-full overflow-hidden">
      <main className="max-w-[1400px] mx-auto h-full flex flex-col justify-center p-2 sm:p-3 lg:px-4 lg:py-6 w-full">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-sm border border-zinc-100 min-h-[500px]">
            <h2 className="text-lg font-semibold mb-4">{isEditing ? 'Edit Mission' : 'Add New Mission'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={currentMission.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g. Innovation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={currentMission.description}
                  onChange={handleInputChange}
                  rows="8"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Mission description..."
                ></textarea>
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all"
                >
                  {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Add Mission'}
                </Button>
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="flex-1 px-4 py-3 bg-black text-white rounded-xl hover:bg-zinc-800 transition-all font-semibold text-sm"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* List Section */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-zinc-100 flex flex-col min-h-[500px]">
            <div className="p-4 border-b border-zinc-100 bg-gradient-to-r from-zinc-100 to-zinc-50">
              <h2 className="text-lg font-semibold text-gray-800">Existing Missions</h2>
            </div>
            <div className="p-4 flex-1 overflow-auto">
              {missions.length === 0 ? (
                <div className="text-center text-gray-500 mt-10">
                  <p>No missions found.</p>
                  <p className="text-sm mt-2">Add a new mission using the form.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {missions.map((mission, index) => (
                    <div key={mission.rcid || mission.id || index} className="p-4 border border-zinc-200 rounded-lg flex justify-between items-start hover:bg-zinc-50 transition-colors">
                      <div className="flex-1 pr-4">
                        <h3 className="font-bold text-gray-900">{mission.title}</h3>
                        <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">{mission.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(mission)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(mission)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <CommonToastContainer />
      
      <DeleteModal 
        isOpen={!!missionToDelete} 
        onClose={() => setMissionToDelete(null)} 
        onConfirm={handleConfirmDelete} 
        title="Delete Mission"
        message="Are you sure you want to delete this mission?"
        confirmText="Yes, Delete"
      />
    </div>
  );
};

export default MissionAdmin;
